/**
 * Shared Patient Form Schemas
 *
 * Zod schemas for patient registration and edit forms.
 * These handle form-level validation (string inputs, UI formatting)
 * while @repo/shared schemas handle API-level validation.
 */

import { z } from 'zod'
import { cleanPhoneNumber } from '@/features/shared/utils/phoneUtils'

// ============================================================================
// Patient Form Schemas
// ============================================================================

/** Base patient fields shared between create and update forms */
const patientFormBase = {
  full_name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  birth_date: z.string().optional(),
  address: z.string().optional(),
  emergency_contact: z.string().optional(),
  emergency_contact_country_code: z.string().default('55'),
  condition_description: z.string().optional(),
  medical_history: z.string().optional(),
  current_medications: z.string().optional(),
}

export const createPatientFormSchema = z.object({
  ...patientFormBase,
  birth_date: z.string().min(1, 'Data de nascimento é obrigatória'),
})

export type CreatePatientFormData = z.infer<typeof createPatientFormSchema>

export const updatePatientFormSchema = z.object({
  ...patientFormBase,
  status: z.enum(['active', 'inactive', 'discharged', 'deleted']),
})

export type UpdatePatientFormData = z.infer<typeof updatePatientFormSchema>

// ============================================================================
// Family Member Form Schema
// ============================================================================

export const familyMemberFormSchema = z
  .object({
    name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
    email: z.string().email('Email inválido').optional().or(z.literal('')),
    phone: z.string().optional().or(z.literal('')),
    country_code: z.string().default('55'),
    relationship: z.string().min(1, 'Parentesco é obrigatório'),
    is_primary_contact: z.boolean().default(false),
    accepts_notifications: z.boolean().default(false),
  })
  .refine(
    (data) => {
      if (!data.phone) return true
      const digits = cleanPhoneNumber(data.phone)
      if (data.country_code === '55') {
        return digits.length >= 10 && digits.length <= 11
      }
      return digits.length >= 4
    },
    {
      message: 'Número de telefone inválido',
      path: ['phone'],
    }
  )
  .refine(
    (data) => {
      // If accepting notifications, whatsapp number is required
      if (data.accepts_notifications && !data.phone) {
        return false
      }
      return true
    },
    {
      message: 'Número de WhatsApp é obrigatório para ativar notificações',
      path: ['accepts_notifications'],
    }
  )

export type FamilyMemberFormData = z.infer<typeof familyMemberFormSchema>
